import { Component, Type, effect, OnDestroy, OnInit, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';

// Components
import { AppDataBrowser } from '@components/app/data-browser/data-browser';
import { AppDictKeyEdit } from '@components/app/dict-key-edit/dict-key-edit';
import { MyapLivekit } from '@components/myap/livekit/livekit';
import { UiDialog } from '@components/ui/dialog/dialog';
import { UiLoading } from '@components/ui/loading/loading';
import { UiToast } from '@components/ui/toast/toast';

// Layouts
import { TopbarLayout } from '@layouts/topbar/topbar';
import { SidebarLayout } from '@layouts/sidebar/sidebar';

// Services
import { AppConfigService } from '@core/services/app-config.service';
import { MainViewService } from '@core/services/main-view.service';
import { ModuleService } from '@core/services/module.service';
import { MyapLivekitService } from '@core/services/myap/livekit.service';
import { MyapLivekitSessionService } from '@core/services/myap/livekit-session.service';
import { ComponentRegistryService } from '@core/services/component-registry.service';
import { SessionService } from '@core/services/session.service';
import { SidebarService } from '@core/services/layouts/sidebar.service';
import { ToastService } from '@core/services/toast.service';
import { TopbarService } from '@core/services/layouts/topbar.service';

// Utils
import { Dic } from '@core/utils/dic.utils';

// PrimeNG
import { ConfirmDialogModule } from 'primeng/confirmdialog';

/**
 * Page principale — shell complet (sidebar, topbar) et composants métier, recouvert par un
 * écran de démarrage tant que `shellReady` est faux. Ouvre seule, au chargement, le
 * changement de mot de passe (`PASSWORD_FORGOTTEN`) ou l'appel d'un lien `livekit=<clé>`.
 */
@Component({
	selector: 'page-app',
	standalone: true,
	imports: [
		NgComponentOutlet,
		AppDataBrowser,
		AppDictKeyEdit,
		MyapLivekit,
		UiDialog,
		UiLoading,
		UiToast,
		TopbarLayout,
		SidebarLayout,
		ConfirmDialogModule,
	],
	templateUrl: './app.html',
	styleUrl: './app.scss',
})
export class AppPage implements OnInit, OnDestroy {
	readonly currentView;
	readonly dynamicComponent = signal<Type<any> | null>(null);
	readonly componentLoading = signal(false);
	protected readonly Dic = Dic;

	readonly shellReady = signal(false);

	private readonly readyTimeoutMs = 8000;
	private readyTimeout?: ReturnType<typeof setTimeout>;

	constructor(
		private mainView: MainViewService,
		public moduleService: ModuleService,
		private appConfigService: AppConfigService,
		public componentRegistry: ComponentRegistryService,
		private livekitService: MyapLivekitService,
		private livekitSessionService: MyapLivekitSessionService,
		private sessionService: SessionService,
		public sidebar: SidebarService,
		private toastService: ToastService,
		private topbarService: TopbarService,
	) {
		this.currentView = this.mainView.viewItem;
		effect((onCleanup) => {
			const state = this.mainView.state();
			const xtype = state?.kind === 'component' ? state.xtype : null;
			let active = true;
			onCleanup(() => {
				active = false;
			});
			this.dynamicComponent.set(null);
			this.componentLoading.set(!!xtype);
			if (!xtype) return;
			void this.componentRegistry.load(xtype, false).then((component) => {
				if (!active) return;
				this.dynamicComponent.set(component);
				this.componentLoading.set(false);
			});
		});

		effect(() => {
			if (this.topbarService.loaded() && this.sidebar.menuLoaded()) this.markShellReady();
		});
	}

	ngOnInit(): void {
		this.readyTimeout = setTimeout(() => this.markShellReady(), this.readyTimeoutMs);

		if (this.sessionService.get<string>('passwordupdate') === 'PASSWORD_FORGOTTEN') {
			this.moduleService.openModule('app-register-password');
		}

		const defaultRoute = this.sessionService.popDefaultRoute();
		if (defaultRoute) {
			setTimeout(() => this.moduleService.openModule(defaultRoute));
		}

		const message = this.sessionService.popMessage();
		if (message) {
			setTimeout(() => this.toastService.success(message));
		}

		this.openCallFromLink();
	}

	ngOnDestroy(): void {
		clearTimeout(this.readyTimeout);
	}

	private openCallFromLink(): void {
		const key = this.appConfigService.popInitParam('livekit');
		if (!key) return;

		this.livekitService.joinByKey(key).subscribe({
			next: (access) => {
				this.livekitSessionService.open({
					url: access.url,
					token: access.token,
					callId: access.id,
					subject: access.subject,
					isOrganizer: access.isorganizer,
					microphoneId: '',
					cameraId: '',
					startMuted: false,
					startCameraOff: false,
				});
			},
			error: (err: any) => {
				console.error('Impossible de rejoindre l’appel:', err);
				this.toastService.error(Dic.joinfailed.text);
			},
		});
	}

	private markShellReady(): void {
		clearTimeout(this.readyTimeout);
		this.shellReady.set(true);
	}
}
